import { PortfolioGroupedItem } from "../models/PortfolioGroupedItem";
import { AssetType } from "../../../shared/types/AssetType";
import { PositionStateFilter } from "../../../shared/types/PositionStateFilter";
import { TargetPlatform } from "../../../shared/types/TargetPlatform";
import { assetService } from "../../../shared/services/AssetService";
import { mathCalculationService } from "../../../shared/services/MathCalculationService";

export type PositionFilters = {
    positionState: PositionStateFilter;
    assetType: AssetType | null;
    targetPlatform: TargetPlatform | null;
};

export type PositionFilterAvailability = {
    positionStates: ReadonlySet<PositionStateFilter>;
    assetTypes: ReadonlySet<AssetType>;
    targetPlatforms: ReadonlySet<TargetPlatform>;
};

function isOpen(position: PortfolioGroupedItem): boolean {
    return position.platformAllocations.some(allocation =>
        mathCalculationService.round(allocation.currentSize, 6) !== 0) &&
        !(assetService.isGroupedOptions(position) &&
            assetService.isGroupedOptionsExpired(position));
}

function matchesState(
    position: PortfolioGroupedItem,
    positionState: PositionStateFilter,
): boolean {
    if (positionState === PositionStateFilter.All) {
        return true;
    }

    return positionState === PositionStateFilter.Open
        ? isOpen(position)
        : !isOpen(position);
}

function apply(
    positions: PortfolioGroupedItem[],
    filters: Pick<PositionFilters, "positionState" | "assetType">,
): PortfolioGroupedItem[] {
    return positions.filter(position =>
        matchesState(position, filters.positionState) &&
        (filters.assetType === null || position.type === filters.assetType));
}

function getAvailability(
    positions: PortfolioGroupedItem[],
    positionsByPlatform: PortfolioGroupedItem[],
    filters: PositionFilters,
): PositionFilterAvailability {
    const positionStates = new Set<PositionStateFilter>();
    const assetTypes = new Set<AssetType>();
    const targetPlatforms = new Set<TargetPlatform>();

    positions.forEach(position => {
        if (filters.assetType === null || position.type === filters.assetType) {
            positionStates.add(isOpen(position)
                ? PositionStateFilter.Open
                : PositionStateFilter.Closed);
        }

        if (matchesState(position, filters.positionState)) {
            assetTypes.add(position.type);
        }
    });

    positionsByPlatform.forEach(position => {
        if (
            matchesState(position, filters.positionState) &&
            (filters.assetType === null || position.type === filters.assetType)
        ) {
            targetPlatforms.add(position.targetPlatform);
        }
    });

    return {
        positionStates,
        assetTypes,
        targetPlatforms,
    };
}

function normalize(
    filters: PositionFilters,
    availability: PositionFilterAvailability,
): PositionFilters {
    return {
        positionState: filters.positionState === PositionStateFilter.All ||
            availability.positionStates.has(filters.positionState)
            ? filters.positionState
            : PositionStateFilter.All,
        assetType: filters.assetType !== null && availability.assetTypes.has(filters.assetType)
            ? filters.assetType
            : null,
        targetPlatform: filters.targetPlatform !== null &&
            availability.targetPlatforms.has(filters.targetPlatform)
            ? filters.targetPlatform
            : null,
    };
}

function areEqual(left: PositionFilters, right: PositionFilters): boolean {
    return left.positionState === right.positionState &&
        left.assetType === right.assetType &&
        left.targetPlatform === right.targetPlatform;
}

export const positionFilterUtils = {
    apply,
    getAvailability,
    normalize,
    areEqual,
    isOpen,
};
